import { Theme } from '@mui/material';
import { TFunction } from 'i18next';
import { ComponentConfig } from '@/components/componentRegistry';
import type {
  MappedLoanOfficer,
  MappedLoanOfficersResponse,
} from '@/services/mappers/loanOfficer.mapper';
import type { MappedBranchesResponse } from '@/services/mappers/branch.mapper';

interface LoanOfficerConfigParams {
  theme: Theme;
  t: TFunction;
  onOfficerSelect: (officerId: string) => void;
  selectedOfficer: string;
  onNext: () => void;
  onNoOfficer: () => void;
  loanOfficers: MappedLoanOfficer[];
  onSearchChange: (searchTerm: string) => void;
  isLoadingOfficers: boolean;
  hasOfficerInContext: boolean;
  selectedOfficerData?: MappedLoanOfficer;
  onLoanOfficersLoaded: (response: MappedLoanOfficersResponse | null) => void;
  onBranchDataLoaded: (response: MappedBranchesResponse | null) => void;
  branchId?: string;
  handleLoader: (loading: boolean) => void;
}

export const getLoanOfficerConfig = ({
  theme,
  t,
  onOfficerSelect,
  selectedOfficer,
  onNext,
  onNoOfficer,
  loanOfficers,
  onSearchChange,
  isLoadingOfficers,
  hasOfficerInContext,
  selectedOfficerData,
  onLoanOfficersLoaded,
  onBranchDataLoaded,
  branchId,
  handleLoader,
}: LoanOfficerConfigParams): ComponentConfig => {
  // Transform loan officers to dropdown options
  const officerOptions = loanOfficers.map((officer) => ({
    label: officer.name,
    value: officer.id,
    nmls: officer.nmls,
  }));

  return {
    component: 'Box',
    props: {
      sx: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      },
    },
    // API calls on mount - fetches loan officers and branch data, stores in Redux via callbacks
    api: [
      {
        service: 'getLoanOfficers',
        args: { fetchAll: true, branchId: branchId || '1040' },
        mapper: 'mapLoanOfficersToComponentFormat',
        onDataLoaded: onLoanOfficersLoaded as (response: unknown) => void,
        handleLoader: handleLoader,
      },
      {
        service: 'getBranches',
        args: { branchId: branchId || '1040' },
        mapper: 'mapBranchesToComponentFormat',
        onDataLoaded: onBranchDataLoaded as (response: unknown) => void,
      },
    ],
    children: [
      // Content Box - Flexible space
      {
        component: 'Box',
        props: {
          sx: {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: { xs: theme.unit(4), md: theme.spacing(4) },
          },
        },
        children: [
          {
            component: 'Card',
            props: {
              sx: {
                width: '100%',
                maxWidth: { xs: '100%', sm: '90%', md: '877px' },
                borderRadius: theme.unit(2.5),
                border: `1px solid ${theme.palette.M3?.sys?.light?.['card-border']}`,
                paddingY: { xs: theme.unit(6), md: theme.unit(12) },
                paddingX: {
                  xs: theme.unit(4),
                  sm: theme.unit(8),
                  md: theme.unit(16),
                },
                backgroundColor: theme.palette.M3?.white,
                boxShadow:
                  '0px 2px 6px 2px #00000014, 0px 1px 2px 0px #00000014',
              },
            },
            children: [
              {
                component: 'ColumnLayout',
                props: {
                  gap: { xs: theme.unit(6), md: theme.unit(12) },
                  fullWidth: true,
                },
                children: [
                  {
                    component: 'ColumnLayout',
                    props: {
                      gap: theme.unit(5),
                      fullWidth: true,
                    },
                    children: [
                      {
                        component: 'ColumnLayout',
                        props: {
                          gap: theme.unit(8),
                          fullWidth: true,
                          justifyContent: 'center',
                        },
                        children: [
                          {
                            component: 'Typography',
                            props: {
                              sx: {
                                ...theme.typography.M3.title.medium,
                                fontWeight: 700,
                                fontSize: {
                                  xs: theme.unit(5),
                                  md: theme.unit(6),
                                },
                                color:
                                  theme.palette.M3?.sys?.light?.[
                                    'on-primary-fixed'
                                  ],
                                textAlign: 'center',
                              },
                              children: t('PageLoanOfficer.title'),
                            },
                          },
                          {
                            component: 'Box',
                            props: {
                              sx: {
                                width: '100%',
                              },
                            },
                            children: [
                              {
                                component: 'SingleSelect',
                                props: {
                                  label: t('PageLoanOfficer.loanOfficerLabel'),
                                  placeholder: t(
                                    'PageLoanOfficer.loanOfficerPlaceholder'
                                  ),
                                  options: officerOptions,
                                  searchable: true,
                                  value: selectedOfficer,
                                  onChange: onOfficerSelect,
                                  onInputChange: onSearchChange,
                                  loading: isLoadingOfficers,
                                  loadingText: 'Loading...',
                                  formControlProps: {
                                    width: '100%',
                                    sx: {
                                      width: '100%',
                                      '& .MuiAutocomplete-root': {
                                        width: '100%',
                                      },
                                      '& .MuiInputBase-root': {
                                        width: '100%',
                                      },
                                    },
                                  },
                                  InputLabelProps: {
                                    sx: {
                                      ...theme.typography.M3.body.small,
                                      fontWeight: 400,
                                      color:
                                        theme.palette.M3?.sys?.light?.[
                                          'on-surface-variant'
                                        ],
                                    },
                                  },
                                  selectProps: {
                                    sx: {
                                      '& .MuiSelect-select': {
                                        ...theme.typography.M3.body.large,
                                        fontWeight: 400,
                                      },
                                    },
                                  },
                                },
                              },
                            ],
                          },
                        ],
                      },
                      // Selected Officer Display Box - Only shown when officer data exists in context
                      ...(hasOfficerInContext && selectedOfficer
                        ? [
                            {
                              component: 'Box',
                              props: {
                                sx: {
                                  width: '100%',
                                  borderRadius: theme.unit(2),
                                  border: `1px solid ${theme.palette.M3?.sys?.light?.outline}`,
                                  padding: theme.unit(4),
                                  backgroundColor:
                                    theme.palette.M3?.sys?.light?.background,
                                },
                              },
                              children: [
                                {
                                  component: 'Typography',
                                  props: {
                                    sx: {
                                      ...theme.typography.M3.title.medium,
                                      fontWeight: 700,
                                      fontSize: theme.unit(4),
                                      color:
                                        theme.palette.M3?.sys?.light?.[
                                          'on-primary-fixed'
                                        ],
                                    },
                                    children:
                                      selectedOfficerData?.name ||
                                      officerOptions.find(
                                        (officer) =>
                                          officer.value === selectedOfficer
                                      )?.label ||
                                      '',
                                  },
                                },
                              ],
                            },
                          ]
                        : []),
                    ],
                  },
                  {
                    component: 'RowLayout',
                    props: {
                      justifyContent: 'space-between',
                      fullWidth: true,
                      sx: {
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: theme.unit(3), sm: theme.unit(4) },
                      },
                    },
                    children: [
                      {
                        component: 'Button',
                        props: {
                          variant: 'outlined',
                          onClick: onNoOfficer,
                          sx: {
                            width: { xs: '100%', sm: '115px' },
                            paddingY: theme.unit(1),
                            paddingX: theme.unit(2.5),
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            ...theme.typography.M3.label.mediumProminent,

                            order: { xs: 2, sm: 1 },
                            height: { xs: theme.unit(12), sm: 'auto' },
                          },
                          children: t('PageLoanOfficer.noOfficerButton'),
                        },
                      },
                      {
                        component: 'Button',
                        props: {
                          variant: 'filled',
                          disabled: !selectedOfficer,
                          onClick: onNext,
                          sx: {
                            width: { xs: '100%', sm: '160px' },
                            borderRadius: theme.unit(3),
                            paddingY: theme.unit(2.5),
                            paddingX: theme.unit(6),
                            textTransform: 'none',
                            ...theme.typography.M3.label.large,
                            order: { xs: 1, sm: 2 },
                            height: { xs: theme.unit(12), sm: 'auto' },
                          },
                          children: t('PageLoanOfficer.nextButton'),
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
};
